<template>
    <rkm-form
        ref="RkmForm"
        show-legend
    >
        <rkm-overlay :show="loading">
            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-select
                        :disabled="classificacao_id && classificacao_id !== enums.Classificacao.OUTRO_MUNICIPIO"
                        :options="form.estados"
                        :validations="validations.estado_id"
                        label="UF"
                        show-default-option
                        text-field="uf"
                        v-model="model.estado_id"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input-autocomplete
                        :disabled="!model.estado_id || (classificacao_id && classificacao_id !== enums.Classificacao.OUTRO_MUNICIPIO)"
                        :fields="fields.municipio"
                        :repository="repositories.comum.municipio"
                        :request-params="requestParams"
                        :selected-text="model.municipio_nome"
                        :validations="validations.municipio_id"
                        @selected-item="handleSelectedItem"
                        label="Município"
                        v-model="model.municipio_id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="9">
                    <rkm-form-input-autocomplete
                        :disabled="!model.municipio_id"
                        :fields="fields.bairro"
                        :repository="repositories.cadastro.bairro"
                        :request-params="{ municipio_id: model.municipio_id }"
                        :selectedText="model.bairro_nome"
                        :validations="validations.bairro_id"
                        label="Bairro"
                        v-model="model.bairro_id"
                    >
                        <rkm-input-group-append
                            v-if="authorize('cadastro-bairro-store')"
                            slot="append"
                        >
                            <rkm-button
                                :disabled="!model.municipio_id"
                                @click="$refs.ModalCadastroBairro.open({
                                    estado_id: model.estado_id,
                                    municipio_id: model.municipio_id,
                                    municipio_nome: model.municipio_nome,
                                })"
                                color="primary"
                                title="Cadastrar bairro"
                                v-rkm-tooltip.hover
                            >
                                <rkm-icon icon="plus" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-input-autocomplete>
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        :disabled="!model.bairro_id"
                        :validations="validations.cep"
                        label="CEP"
                        placeholder="99.999-999"
                        v-mask="utils.mask.cep"
                        v-model="model.cep"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-select
                        :disabled="!model.bairro_id"
                        :options="form.tipos"
                        :validations="validations.tipo_id"
                        label="Tipo"
                        show-default-option
                        v-model="model.tipo_id"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        :disabled="!model.bairro_id"
                        :validations="validations.nome"
                        label="Nome"
                        uppercase
                        v-model="model.nome"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-overlay>

        <ModalCadastroBairro
            @save="handleBairroSave"
            ref="ModalCadastroBairro"
        />
    </rkm-form>
</template>

<script>
// Components.
import ModalCadastroBairro from '@/components/cadastro/bairro/Modal'

// Enums.
import {Classificacao} from '@/enums'

// Mixins.
import {authorization, error} from '@/mixins'

// Utils.
import mask from '@/utils/mask'
import unmask from '@/utils/unmask'

// Vuex.
import {mapActions, mapGetters} from 'vuex'

export default {
    components: {ModalCadastroBairro},
    computed: {
        ...mapGetters(['getLoading']),
        ...mapGetters({cliente: 'getCliente'}),
        fields() {
            return {
                municipio: [
                    { key: 'nome', name: 'cadastro_municipio.nome' },
                ],
                bairro: [
                    { key: 'nome', name: 'cadastro_bairro.nome' },
                ],
            }
        },
        loading() {
            return this.getLoading('cadastro-logradouro-form')
        },
        requestParams() {
            let params = {estado_id: this.model.estado_id}

            if (this.classificacao_id) {
                params.classificacao_id = this.classificacao_id
            }

            return params
        },
        validations() {
            return {
                estado_id: {required: true},
                municipio_id: {required: true},
                bairro_id: {required: true},
                tipo_id: {required: true},
                nome: {
                    required: true,
                    min: 3,
                    max: 250,
                },
                cep: {
                    required: true,
                    min: 10,
                    max: 10,
                },
            }
        },
    },
    data: () => ({
        enums: {Classificacao},
        form: {
            estados: [],
            tipos: [],
        },
        model: {
            estado_id: null,
            municipio_id: null,
            municipio_nome: null,
            bairro_id: null,
            bairro_nome: null,
            tipo_id: null,
            nome: null,
            cep: null,
        },
        utils: {mask, unmask},
    }),
    inject: ['repositories'],
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        handleBairroSave(bairro) {
            this.model.bairro_id = bairro.id
            this.model.bairro_nome = bairro.nome
        },
        handleSelectedItem(municipio) {
            this.model.municipio_nome = municipio.nome
        },  
        load() {
            this.setLoading('cadastro-logradouro-form')

            if (this.id) {
                this.repositories.cadastro.logradouro.edit(this.id)
                    .then(({ data }) => {
                        this.form.estados = data.form.estados
                        this.form.tipos = data.form.tipos
                        this.model.estado_id = data.model.estado_id
                        this.model.estado_nome = data.model.estado_nome
                        this.model.municipio_id = data.model.municipio_id
                        this.model.municipio_nome = data.model.municipio_nome
                        this.model.bairro_id = data.model.bairro_id
                        this.model.bairro_nome = data.model.bairro_nome
                        this.model.tipo_id = data.model.tipo_id
                        this.model.tipo_nome = data.model.tipo_nome
                        this.model.nome = data.model.nome
                        this.model.cep = data.model.cep
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading('cadastro-logradouro-form'))
            } else {
                this.repositories.cadastro.logradouro.create()
                    .then(({ data }) => {
                        this.form.estados = data.form.estados
                        this.form.tipos = data.form.tipos

                        if (this.classificacao_id !== this.enums.Classificacao.OUTRO_MUNICIPIO) {
                            this.model.estado_id = this.cliente.municipio.estado.id || null
                            this.model.municipio_id = this.cliente.municipio.id || null
                            this.model.municipio_nome = this.cliente.municipio.nome || null
                        }
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading('cadastro-logradouro-form'))
            }
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(validated => {
                    if (validated) {
                        this.setLoading('cadastro-logradouro-form')

                        const request = {
                            bairro_id: this.model.bairro_id,
                            tipo_id: this.model.tipo_id,
                            nome: this.model.nome,
                            cep: this.utils.unmask.cep(this.model.cep),
                        }

                        if (this.id) {
                            this.repositories.cadastro.logradouro.update(request, this.id)
                                .then(({ data }) => this.$emit('save', data))
                                .catch(this.handleError)
                                .finally(() => this.unsetLoading('cadastro-logradouro-form'))
                        } else {
                            this.repositories.cadastro.logradouro.store(request)
                                .then(({ data }) => this.$emit('save', data))
                                .catch(this.handleError)
                                .finally(() => this.unsetLoading('cadastro-logradouro-form'))
                        }
                    }
                })
        },
    },
    mixins: [authorization, error],
    mounted() {
        this.load()
    },
    props: {
        classificacao_id: {type: Number},
        id: {type: Number},
    },
}
</script>